import { csv } from '../RmMonitor/liveModel';

export const PAGE_ID = 'hotel-deal-performance';
export const VIEWS = [
  'Deal Performance View',
  'Location Results',
  'Property Run History View',
  'Deals Closing View',
] as const;

export const DEAL_CARD = 'deals';
export const TRAFFIC_CARD = 'traffic';
export const MARKET_CARD = 'market';
export const CLOSING_CARD = 'closing';
export const VENDOR_DAILY_CARD = 'vendor-daily';
export const LOCATION_CARD = 'location';
export const LOCATION_FROM = '2017-03-30';
export const LOCATION_TO = '2023-09-14';
export const ALL_DEALS = '__ALL__';
export const NO_VENDOR = '__NONE__';
export const SAVED_FROM = '2021-09-29';
export const SAVED_TO = '2023-09-11';

export type EndedFilter = 'all' | 'yes' | 'no';

export type DealRow = {
  dealId: string;
  opportunity: string;
  offerRegion: string;
  dealTier: string;
  vendor: string;
  offerCountry: string;
  salesCurrency: string;
  startDate: string;
  endDate: string;
  countdown: number;
  daysLive: number | null;
  grossSales: number;
  refunds: number;
  grossMargin: number;
  vendorRevenue: number;
  totalOrders: number;
  refundedOrders: number;
  packages: number;
  nights: number;
  pageViews: number;
  uniqueViews: number;
};

export type TrafficRow = {
  customerRegion: string;
  grossSales: number;
  totalOrders: number;
  share: number | null;
  orderShare: number | null;
};

export type MarketRow = {
  market: string;
  grossSales: number;
  share: number | null;
};

export type ClosingRow = {
  index: number;
  vendor: string;
  offerCountry: string;
  endDate: string;
  grossSales: number;
  refunds: number;
  grossMargin: number;
  vendorRevenue: number;
  netOrders: number;
  nights: number;
};

export type LocationRow = {
  name: string;
  grossSales: number;
  refunds: number;
  grossMargin: number;
  vendorRevenue: number;
  totalOrders: number;
};

export type VendorRow = {
  vendor: string;
  mostRecentStart: string;
  dealsRun: number;
  grossSales: number;
};

export type DailyRow = {
  saleDate: string;
  grossSales: number;
  grossMargin: number;
  totalOrders: number;
  cumulativeSales: number;
};

const DEAL_CSV = [
  'Opportunity Name',
  'Deal Id',
  'Offer Region',
  'Deal Tier',
  'Countdown Days',
  'Days Live',
  'Gross Sales',
  'Page Views',
  'Revenue/View',
  'Conversion',
] as const;

function unwrap(value: unknown): unknown {
  if (value && typeof value === 'object' && 'value' in value) return (value as { value: unknown }).value;
  return value;
}

function text(value: unknown): string {
  const raw = unwrap(value);
  if (raw == null) return '';
  return String(raw);
}

function num(value: unknown): number {
  const raw = unwrap(value);
  if (raw == null || raw === '') return 0;
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sydneyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

function shift(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function dayDiff(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

export function countdownDays(endDate: string, today: string): number {
  if (!endDate) return 0;
  return dayDiff(today, endDate);
}

export function daysLive(startDate: string, today: string): number | null {
  if (!startDate) return null;
  return dayDiff(startDate, shift(today, -1));
}

export function revenuePerView(grossSales: number, pageViews: number): number | null {
  if (!pageViews) return null;
  return grossSales / pageViews;
}

export function conversion(packages: number, uniqueViews: number): number | null {
  if (!uniqueViews) return null;
  return packages / uniqueViews;
}

export function formatMoney(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '';
  const rounded = Math.round(value);
  const body = Math.abs(rounded).toLocaleString('en-AU');
  return rounded < 0 ? `-$${body}` : `$${body}`;
}

export function formatMoney2(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '';
  const sign = value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatInteger(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '';
  return Math.round(value).toLocaleString('en-AU');
}

export function formatPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '';
  return `${(value * 100).toFixed(2)}%`;
}

export function readDeals(rows: ReadonlyArray<Record<string, unknown>>, today = sydneyToday()): DealRow[] {
  return rows.map((row) => {
    const endDate = text(row.end_date);
    const startDate = text(row.start_date);
    return {
      dealId: text(row.deal_id),
      opportunity: text(row.opportunity) || 'Null',
      offerRegion: text(row.offer_region) || 'Null',
      dealTier: text(row.deal_tier) || 'Null',
      vendor: text(row.vendor) || 'Null',
      offerCountry: text(row.offer_country) || 'Null',
      salesCurrency: text(row.sales_currency) || 'Null',
      startDate,
      endDate,
      countdown: countdownDays(endDate, today),
      daysLive: daysLive(startDate, today),
      grossSales: num(row.gross_sales),
      refunds: num(row.refunds),
      grossMargin: num(row.gross_margin),
      vendorRevenue: num(row.vendor_revenue),
      totalOrders: num(row.total_orders),
      refundedOrders: num(row.refunded_orders),
      packages: num(row.packages),
      nights: num(row.nights),
      pageViews: num(row.page_views),
      uniqueViews: num(row.unique_views),
    };
  });
}

export function filterDeals(rows: readonly DealRow[], ended: EndedFilter): DealRow[] {
  if (ended === 'all') return [...rows];
  if (ended === 'yes') return rows.filter((row) => row.countdown <= 0);
  return rows.filter((row) => row.countdown > 0);
}

function byText(left: string, right: string): number {
  return left.localeCompare(right, 'en');
}

export function sortDeals(rows: readonly DealRow[]): DealRow[] {
  const minCountdown = new Map<string, number>();
  rows.forEach((row) => {
    const current = minCountdown.get(row.opportunity);
    if (current === undefined || row.countdown < current) minCountdown.set(row.opportunity, row.countdown);
  });
  return [...rows].sort((left, right) => {
    const opportunity =
      (minCountdown.get(left.opportunity) ?? 0) - (minCountdown.get(right.opportunity) ?? 0) ||
      byText(left.opportunity, right.opportunity);
    if (opportunity) return opportunity;
    return (
      left.countdown - right.countdown ||
      byText(left.dealId, right.dealId) ||
      byText(left.offerRegion, right.offerRegion) ||
      byText(left.dealTier, right.dealTier)
    );
  });
}

export function dealSummaryCsv(rows: readonly DealRow[]): string {
  return csv(
    sortDeals(rows).map((row) => ({
      'Opportunity Name': row.opportunity,
      'Deal Id': row.dealId,
      'Offer Region': row.offerRegion,
      'Deal Tier': row.dealTier,
      'Countdown Days': row.countdown,
      'Days Live': row.daysLive ?? '',
      'Gross Sales': row.grossSales,
      'Page Views': row.pageViews,
      'Revenue/View': revenuePerView(row.grossSales, row.pageViews) ?? '',
      Conversion: conversion(row.packages, row.uniqueViews) ?? '',
    })),
    [...DEAL_CSV],
  );
}

export function readTraffic(rows: ReadonlyArray<Record<string, unknown>>): TrafficRow[] {
  const parsed = rows.map((row) => ({
    customerRegion: text(row.customer_region) || 'Null',
    grossSales: num(row.gross_sales),
    totalOrders: num(row.total_orders),
  }));
  const sales = parsed.reduce((sum, row) => sum + row.grossSales, 0);
  const orders = parsed.reduce((sum, row) => sum + row.totalOrders, 0);
  return parsed
    .map((row) => ({
      ...row,
      share: sales ? row.grossSales / sales : null,
      orderShare: orders ? row.totalOrders / orders : null,
    }))
    .sort((left, right) => right.grossSales - left.grossSales || byText(left.customerRegion, right.customerRegion));
}

export function readMarket(rows: ReadonlyArray<Record<string, unknown>>): MarketRow[] {
  const parsed = rows.map((row) => ({ market: text(row.market) || 'Info NA', grossSales: num(row.gross_sales) }));
  const sales = parsed.reduce((sum, row) => sum + row.grossSales, 0);
  return parsed
    .map((row) => ({ ...row, share: sales ? row.grossSales / sales : null }))
    .sort((left, right) => byText(left.market, right.market));
}

export function readLocation(rows: ReadonlyArray<Record<string, unknown>>, grain: string): LocationRow[] {
  return rows
    .filter((row) => text(row.grain) === grain)
    .map((row) => ({
      name: text(row.name) || 'Null',
      grossSales: num(row.gross_sales),
      refunds: num(row.refunds),
      grossMargin: num(row.gross_margin),
      vendorRevenue: num(row.vendor_revenue),
      totalOrders: num(row.total_orders),
    }))
    .sort((left, right) => right.grossSales - left.grossSales || byText(left.name, right.name));
}

export function vendorRows(rows: readonly DealRow[]): VendorRow[] {
  const grouped = new Map<string, VendorRow>();
  rows.forEach((row) => {
    const current = grouped.get(row.vendor) ?? {
      vendor: row.vendor,
      mostRecentStart: '',
      dealsRun: 0,
      grossSales: 0,
    };
    current.dealsRun += 1;
    current.grossSales += row.grossSales;
    if (row.startDate > current.mostRecentStart) current.mostRecentStart = row.startDate;
    grouped.set(row.vendor, current);
  });
  return [...grouped.values()].sort(
    (left, right) => byText(right.mostRecentStart, left.mostRecentStart) || byText(left.vendor, right.vendor),
  );
}

export function readClosing(rows: ReadonlyArray<Record<string, unknown>>): ClosingRow[] {
  const parsed = rows.map((row) => ({
    vendor: text(row.vendor) || 'Null',
    offerCountry: text(row.offer_country) || 'Null',
    endDate: text(row.end_date),
    grossSales: num(row.gross_sales),
    refunds: num(row.refunds),
    grossMargin: num(row.gross_margin),
    vendorRevenue: num(row.vendor_revenue),
    netOrders: num(row.net_orders),
    nights: num(row.nights),
  }));
  const vendorSales = new Map<string, number>();
  parsed.forEach((row) => vendorSales.set(row.vendor, (vendorSales.get(row.vendor) ?? 0) + row.grossSales));
  parsed.sort(
    (left, right) =>
      (vendorSales.get(right.vendor) ?? 0) - (vendorSales.get(left.vendor) ?? 0) ||
      byText(left.vendor, right.vendor) ||
      byText(left.offerCountry, right.offerCountry) ||
      byText(left.endDate, right.endDate),
  );
  return parsed.map((row, index) => ({ index: index + 1, ...row }));
}

export function readDaily(rows: ReadonlyArray<Record<string, unknown>>): DailyRow[] {
  const parsed = rows
    .map((row) => ({
      saleDate: text(row.sale_date),
      grossSales: num(row.gross_sales),
      grossMargin: num(row.gross_margin),
      totalOrders: num(row.total_orders),
      cumulativeSales: 0,
    }))
    .filter((row) => row.saleDate)
    .sort((left, right) => byText(left.saleDate, right.saleDate));
  let running = 0;
  return parsed.map((row) => {
    running += row.grossSales;
    return { ...row, cumulativeSales: running };
  });
}

export function grossSalesTotal(rows: readonly { grossSales: number }[]): number {
  return rows.reduce((sum, row) => sum + row.grossSales, 0);
}
